import { z } from 'zod';

import { reportAutomationConfigSchema } from './automation-endpoints';
import { ReportColumn, ReportSortBy, TransactionType } from './common';
import { FinancialReportBookingType } from './financial-report-v2';
import { GuestReportBookingSourceFilter } from './guest-report';
import { ReportDateRange, ReportType } from './report';
import { dateSchema, datetimeSchema } from '../../common';
import { buildQueryString } from '../../utils/querystring';
import { BookingStatus, ItemType } from '../booking/booking';
import { makeEndpoint, paginateQuerySchema, paginateResponseSchema, stringBooleanSchema } from '../common';
import { PaymentMethods } from '../payment-method/public';

const reportSavedConfigBaseSchema = z.object({
  id: z.string(),
  brandId: z.string(),
  name: z.string(),
  range: z.nativeEnum(ReportDateRange),
  rangeFrom: dateSchema.nullable(),
  rangeTo: dateSchema.nullable(),
  sortBy: z.nativeEnum(ReportSortBy),
  experienceId: z.array(z.string()),
  resourceGroupCollectionId: z.array(z.string()),
  reportType: z.nativeEnum(ReportType),
  columns: z.array(z.nativeEnum(ReportColumn)),
  createdById: z.string(),
  categoryIds: z.array(z.string()),
  bookingStatus: z.array(z.nativeEnum(BookingStatus)),
  transactionType: z.array(z.nativeEnum(TransactionType)),
  hostIds: z.array(z.string()),
  itemType: z.array(z.nativeEnum(ItemType)),
  paymentMethod: z.array(z.nativeEnum(PaymentMethods)),
  bookingType: z.array(z.nativeEnum(FinancialReportBookingType)),
  confirmationCode: z.string().nullable(),
  cartId: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  showNegativeDiscountValues: z.boolean().nullable(),
  showNegativeRefundValues: z.boolean().nullable(),
  bookingSources: z.array(z.nativeEnum(GuestReportBookingSourceFilter)),
  createdAt: datetimeSchema,
  updatedAt: datetimeSchema,
});

export const reportSavedConfigSchema = reportSavedConfigBaseSchema.extend({
  automations: z.array(reportAutomationConfigSchema),
});

export const reportSavedConfigListItemSchema = reportSavedConfigBaseSchema.extend({
  automations: z.number(),
});

export type ReportSavedConfigListItem = z.infer<typeof reportSavedConfigListItemSchema>;

const createReportSavedConfigRequestSchema = z.object({
  name: z.string().min(1),
  reportType: z.nativeEnum(ReportType),
  columns: z.array(z.nativeEnum(ReportColumn)),
  sortBy: z.nativeEnum(ReportSortBy),
  experienceId: z.array(z.string()).optional(),
  resourceGroupCollectionId: z.array(z.string()).optional(),
  range: z.nativeEnum(ReportDateRange),
  rangeFrom: dateSchema.nullable().optional(),
  rangeTo: dateSchema.nullable().optional(),
  categoryIds: z.array(z.string()).optional(),
  bookingStatus: z.array(z.nativeEnum(BookingStatus)).optional(),
  transactionType: z.array(z.nativeEnum(TransactionType)).optional(),
  hostIds: z.array(z.string()).optional(),
  itemType: z.array(z.nativeEnum(ItemType)).optional(),
  paymentMethod: z.array(z.nativeEnum(PaymentMethods)).optional(),
  bookingType: z.array(z.nativeEnum(FinancialReportBookingType)).optional(),
  confirmationCode: z.string().nullable().optional(),
  cartId: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  showNegativeDiscountValues: z.boolean().nullable().optional(),
  showNegativeRefundValues: z.boolean().nullable().optional(),
  bookingSources: z.array(z.nativeEnum(GuestReportBookingSourceFilter)).optional(),
});

const updateReportSavedConfigRequestSchema = z.object({
  name: z.string().min(1).optional(),
  columns: z.array(z.nativeEnum(ReportColumn)).optional(),
  sortBy: z.nativeEnum(ReportSortBy),
  experienceId: z.array(z.string()).optional(),
  resourceGroupCollectionId: z.array(z.string()).optional(),
  range: z.nativeEnum(ReportDateRange),
  rangeFrom: dateSchema.nullable().optional(),
  rangeTo: dateSchema.nullable().optional(),
  categoryIds: z.array(z.string()).optional(),
  bookingStatus: z.array(z.nativeEnum(BookingStatus)).optional(),
  transactionType: z.array(z.nativeEnum(TransactionType)).optional(),
  hostIds: z.array(z.string()).optional(),
  itemType: z.array(z.nativeEnum(ItemType)).optional(),
  paymentMethod: z.array(z.nativeEnum(PaymentMethods)).optional(),
  bookingType: z.array(z.nativeEnum(FinancialReportBookingType)).optional(),
  confirmationCode: z.string().nullable().optional(),
  cartId: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  showNegativeDiscountValues: z.boolean().nullable().optional(),
  showNegativeRefundValues: z.boolean().nullable().optional(),
  bookingSources: z.array(z.nativeEnum(GuestReportBookingSourceFilter)).optional(),
});

const listReportSavedConfigsQuerySchema = paginateQuerySchema().extend({
  onlyAutomated: stringBooleanSchema.optional(),
});

export type ListReportSavedConfigsQuerySchema = z.infer<typeof listReportSavedConfigsQuerySchema>;

export const CreateReportSavedConfigEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/report-saved-configs`,
  responseSchema: reportSavedConfigSchema,
  requestSchema: createReportSavedConfigRequestSchema,
});

export const ListReportSavedConfigsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, query }: { brandId: string; query: z.input<typeof listReportSavedConfigsQuerySchema> }) =>
    `v1/brands/${brandId}/report-saved-configs?${buildQueryString(query)}`,
  responseSchema: paginateResponseSchema(reportSavedConfigListItemSchema),
  requestSchema: z.void(),
  querySchema: listReportSavedConfigsQuerySchema,
});

export const GetReportSavedConfigEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, configId }: { brandId: string; configId: string }) =>
    `v1/brands/${brandId}/report-saved-configs/${configId}`,
  responseSchema: reportSavedConfigSchema,
  requestSchema: z.void(),
});

export const UpdateReportSavedConfigEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ brandId, configId }: { brandId: string; configId: string }) =>
    `v1/brands/${brandId}/report-saved-configs/${configId}`,
  responseSchema: reportSavedConfigSchema,
  requestSchema: updateReportSavedConfigRequestSchema,
});

export const DeleteReportSavedConfigEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ brandId, configId }: { brandId: string; configId: string }) =>
    `v1/brands/${brandId}/report-saved-configs/${configId}`,
  responseSchema: z.void(),
  requestSchema: z.void(),
});
